import React, { useState, useRef, useEffect } from 'react';
import { ThumbsUp, ThumbsDown } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';

import { FeedbackReason } from './feedback-reason';
import { Love } from '../love';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export type FeedbackResponse = 'accept' | 'reject';

interface FeedbackProps {
  feedback?: FeedbackResponse;
  onFeedback?: (response: FeedbackResponse) => void;
  textConfig?: {
    positiveAnswerText?: string;
    negativeAnswerText?: string;
  };
  disabled?: boolean;
  feedbackReasonLength?: number;
  feedbackReasonPlaceholder?: string;
  onFeedbackReasonSubmit?: (reason: string) => void;
  feedbackReasonThankYouText?: string;
}

export function Feedback({
  feedback,
  onFeedback,
  textConfig = {},
  disabled = false,
  feedbackReasonLength = 500,
  feedbackReasonPlaceholder = 'What could we have done better? Your feedback is valuable to us...',
  onFeedbackReasonSubmit,
  feedbackReasonThankYouText = 'Thank you for your feedback!',
}: FeedbackProps) {
  const [showHearts, setShowHearts] = useState(false);
  const [showFeedbackReason, setShowFeedbackReason] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (showFeedbackReason && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [showFeedbackReason]);

  const handleFeedback = (response: FeedbackResponse) => {
    if (onFeedback) {
      if (response === 'accept') {
        setShowHearts(true);
        setTimeout(() => setShowHearts(false), 2000);
      }
      onFeedback(response);
      if (response === 'reject') {
        setShowFeedbackReason(true);
      }
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-end gap-2" data-name="Feedback">
        <TooltipProvider>
          <Love doAnimation={showHearts}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`rounded-md size-8 flex items-center justify-center transition-all duration-200 transform hover:scale-105 active:scale-95 ${
                    feedback === 'accept' ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'text-dark-500'
                  }`}
                  onClick={() => {
                    handleFeedback('accept');
                    setShowFeedbackReason(false);
                  }}
                  disabled={disabled}
                >
                  <motion.div
                    initial={{ scale: 1 }}
                    animate={{ scale: feedback === 'accept' ? [1, 1.2, 1] : 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ThumbsUp weight="bold" />
                  </motion.div>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{textConfig.positiveAnswerText || 'Helpful'}</p>
              </TooltipContent>
            </Tooltip>
          </Love>
        </TooltipProvider>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={`rounded-md size-8 flex items-center justify-center transition-all duration-200 transform hover:scale-105 active:scale-95 ${
                  feedback === 'reject' ? 'bg-red-100 text-red-600 hover:bg-red-200' : 'text-dark-500'
                }`}
                onClick={() => handleFeedback('reject')}
                disabled={disabled}
              >
                <motion.div
                  initial={{ scale: 1 }}
                  animate={{ scale: feedback === 'reject' ? [1, 1.2, 1] : 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <ThumbsDown weight="bold" />
                </motion.div>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{textConfig.negativeAnswerText || 'Not helpful'}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <AnimatePresence>
        {showFeedbackReason && (
          <FeedbackReason
            open={showFeedbackReason}
            onSubmit={onFeedbackReasonSubmit}
            thankYouText={feedbackReasonThankYouText}
            placeholder={feedbackReasonPlaceholder}
            maxLength={feedbackReasonLength}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
